"use client";

import React, { useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import {
  useSubscribedSymbolsStore,
  useVybeTraderClient,
} from "@/vybe-trader-client";

import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";
import { useSettingsStore } from "@/lib/settings";
import { OneClickTradingDialog } from "@/components/dialogs/OneClickTradingDialog";
import { DENSITY } from "@/lib/density-scale";
import { useControlState } from "@/components/trades-view/trade-order/hooks/useControlState";
import { useIsInvestorAccount } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";

interface ChartTradePanelProps {
  variant?: "mobile" | "desktopOverlay";
}

export default function ChartTradePanel({
  variant = "mobile",
}: ChartTradePanelProps) {
  const isDesktopOverlay = variant === "desktopOverlay";
  const chartPanelId = useChartPanelId();
  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol,
  );
  const { client } = useVybeTraderClient();
  const isInvestor = useIsInvestorAccount();

  // Subscribe to specific symbol data - this will auto-update when quote changes
  const symbolData = useSubscribedSymbolsStore((state) =>
    selectedSymbol ? state.byCurrency.get(selectedSymbol.currency) : undefined
  );

  // Get current quote for selected symbol (reactive subscription)
  const currentQuote = symbolData?.quote || null;

  // State
  const [lotSize, setLotSize] = useState<string>("0.01");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showOneClickDialog, setShowOneClickDialog] = useState(false);
  const [pendingTradeType, setPendingTradeType] = useState<
    "Buy" | "Sell" | null
  >(null);

  // Track individual trade requests (for spam prevention on lot size inputs)
  const [activeTrades, setActiveTrades] = useState<Set<string>>(new Set());

  // Get digits directly from symbol data in store
  const digits = symbolData?.symbol?.digits ?? 5;

  // Get live prices from quote
  const bidPrice = currentQuote?.bid ?? 0;
  const askPrice = currentQuote?.ask ?? 0;

  // Format price using digits from store
  const formatPrice = (price: number): string => {
    if (!price) return "---";
    return price.toFixed(digits);
  };

  // Handle lot size changes
  const handleLotSizeChange = (value: string) => {
    // Allow only numbers and decimal point
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (regex.test(value) || value === "") {
      setLotSize(value);
    }
  };

  // Increment/decrement lot size
  const adjustLotSize = (increment: boolean) => {
    const currentLot = parseFloat(lotSize) || 0;
    const step = 0.01;
    const newLot = increment
      ? currentLot + step
      : Math.max(0.01, currentLot - step);
    setLotSize(newLot.toFixed(2));
  };

  // Handle Market Execution trades
  const handleTrade = async (tradeType: "Buy" | "Sell") => {
    const oneClickTrading = useSettingsStore.getState().getSetting("oneClickTrading");

    if (!oneClickTrading) {
      setPendingTradeType(tradeType);
      setShowOneClickDialog(true);
      return;
    }

    await executeTrade(tradeType);
  };

  // Execute the actual trade
  const executeTrade = async (tradeType: "Buy" | "Sell") => {
    if (!selectedSymbol) {
      setErrorMessage("No symbol selected");
      return;
    }

    if (!client || !client.isConnected()) {
      setErrorMessage("Not connected to server");
      return;
    }

    if (!currentQuote || bidPrice === 0 || askPrice === 0) {
      setErrorMessage("No quote available");
      return;
    }

    const lots = parseFloat(lotSize);
    if (isNaN(lots) || lots <= 0) {
      setErrorMessage("Invalid lot size");
      return;
    }

    // Generate unique trade ID for tracking
    const tradeId = `${tradeType}-${Date.now()}-${Math.random()}`;

    // Add to active trades (for UI feedback)
    setActiveTrades((prev) => new Set(prev).add(tradeId));
    setErrorMessage("");

    try {
      // Market execution uses ask price for buy, bid price for sell
      const orderPrice = tradeType === "Buy" ? askPrice : bidPrice;

      const orderData = {
        Symbol: selectedSymbol.currency,
        Lots: lots,
        OrderType: tradeType == "Buy" ? 0 : 1,
        Price: orderPrice,
        StopLoss: 0, // No SL/TP for quick trades from chart panel
        TakeProfit: 0,
        Slippage: 100, // Default slippage
        Comment: `MT6 Chart Trade (${tradeId.slice(-8)})`, // Add trade ID to comment for tracking
      };

      // Fire and forget - don't wait for response to allow spamming
      client.sendOrder(orderData).catch((error) => {
        console.error(`[ChartTradePanel] Trade ${tradeId} failed:`, error);
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to place order"
        );
      });

      // Don't reset lot size anymore to allow rapid multiple trades
    } catch (error) {
      console.error(`[ChartTradePanel] Trading error for ${tradeId}:`, error);
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to place order"
      );
    } finally {
      // Remove from active trades after a short delay (for UI feedback)
      setTimeout(() => {
        setActiveTrades((prev) => {
          const newSet = new Set(prev);
          newSet.delete(tradeId);
          return newSet;
        });
      }, 500); // Short delay to show the trade was submitted
    }
  };

  // Shared control-state model (proposal 10). Buttons disable without a quote;
  // the lot input + steppers disable once too many trades are in flight.
  const sellControl = useControlState({
    isDisabled: !currentQuote || bidPrice === 0,
  });
  const buyControl = useControlState({
    isDisabled: !currentQuote || askPrice === 0,
  });
  const lotControl = useControlState({ activeCount: activeTrades.size });

  const handleSellByMarket = () => {
    handleTrade("Sell");
  };

  const handleBuyByMarket = () => {
    handleTrade("Buy");
  };

  // Handle one-click dialog confirmation
  const handleOneClickConfirm = () => {
    if (pendingTradeType) {
      executeTrade(pendingTradeType);
      setPendingTradeType(null);
    }
  };

  // Clear error message after a few seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Investor (read-only) login: no BUY/SELL on the chart at all — the account badge explains why.
  if (isInvestor) return null;

  if (isDesktopOverlay) {
    return (
      <div className="flex flex-col items-stretch gap-1 pointer-events-auto w-full">
        <div className="flex w-full items-stretch gap-1 rounded-md border border-border bg-background/90 px-1 py-1 shadow-sm backdrop-blur-sm">
          <button
            onClick={sellControl.guard(handleSellByMarket)}
            disabled={!currentQuote || bidPrice === 0}
            className={`flex-1 min-w-0 ${DENSITY.ROW_H} rounded bg-red-600 px-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background ${sellControl.className}`}
            title="Sell at market"
            aria-label={`Sell at market, ${formatPrice(bidPrice)}`}
            {...sellControl.ariaAttrs}
          >
            <span className="mr-1.5 font-bold tracking-wide">{formatPrice(bidPrice)}</span>
            <span className="opacity-90">SELL</span>
          </button>

          <div className="flex shrink-0 items-center rounded border border-border bg-background">
            <button
              onClick={lotControl.guard(() => adjustLotSize(false))}
              disabled={lotControl.isDisabled}
              className={`flex ${DENSITY.ROW_H} w-5 items-center justify-center text-muted-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${lotControl.isDisabled ? "is-disabled-glyph" : ""}`}
              title="Decrease lots"
              aria-label="Decrease lots"
              {...lotControl.ariaAttrs}
            >
              <ChevronDown size={12} />
            </button>

            <input
              type="text"
              value={lotSize}
              onChange={(e) => handleLotSizeChange(e.target.value)}
              disabled={lotControl.isDisabled}
              placeholder="0.01"
              aria-label="Lot size"
              className={`${DENSITY.ROW_H} w-10 bg-transparent text-center text-xs font-medium text-foreground tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${lotControl.className}`}
              {...lotControl.ariaAttrs}
            />

            <button
              onClick={lotControl.guard(() => adjustLotSize(true))}
              disabled={lotControl.isDisabled}
              className={`flex ${DENSITY.ROW_H} w-5 items-center justify-center text-muted-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${lotControl.isDisabled ? "is-disabled-glyph" : ""}`}
              title="Increase lots"
              aria-label="Increase lots"
              {...lotControl.ariaAttrs}
            >
              <ChevronUp size={12} />
            </button>
          </div>

          <button
            onClick={buyControl.guard(handleBuyByMarket)}
            disabled={!currentQuote || askPrice === 0}
            className={`flex-1 min-w-0 ${DENSITY.ROW_H} rounded bg-blue-600 px-2 text-xs font-semibold text-white hover:bg-blue-700 transition-colors tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background ${buyControl.className}`}
            title="Buy at market"
            aria-label={`Buy at market, ${formatPrice(askPrice)}`}
            {...buyControl.ariaAttrs}
          >
            <span className="mr-1.5 opacity-90">BUY</span>
            <span className="font-bold tracking-wide">{formatPrice(askPrice)}</span>
          </button>
        </div>

        {errorMessage && (
          <div className="rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-red-400 shadow-sm">
            {errorMessage}
          </div>
        )}

        <OneClickTradingDialog
          open={showOneClickDialog}
          onOpenChange={setShowOneClickDialog}
          onConfirm={handleOneClickConfirm}
        />
      </div>
    );
  }

  return (
    <div className="bg-background border-b border-border">
      <div className="px-3 py-1.5">
        <div className="flex items-center justify-between gap-2">
          {/* SELL Button */}
          <button
            onClick={sellControl.guard(handleSellByMarket)}
            disabled={!currentQuote || bidPrice === 0}
            className={`flex-1 bg-red-600 hover:bg-red-700 text-foreground rounded px-2.5 py-1.5 transition-colors min-h-11 ${sellControl.className}`}
            aria-label={`Sell at market, ${formatPrice(bidPrice)}`}
            {...sellControl.ariaAttrs}
          >
            <div className="text-[10px] opacity-70">SELL</div>
            <div className="text-base font-bold tabular-nums">{formatPrice(bidPrice)}</div>
          </button>

          {/* Lot Size Input */}
          <div className="flex items-center bg-background rounded min-h-11">
            <button
              onClick={lotControl.guard(() => adjustLotSize(false))}
              className={`p-1.5 hover:bg-gray-800 transition-colors min-w-11 min-h-11 flex items-center justify-center ${lotControl.isDisabled ? "is-disabled-glyph" : ""}`}
              disabled={lotControl.isDisabled}
              aria-label="Decrease lots"
              {...lotControl.ariaAttrs}
            >
              <ChevronDown size={14} />
            </button>

            <input
              type="text"
              value={lotSize}
              onChange={(e) => handleLotSizeChange(e.target.value)}
              className={`w-16 bg-transparent text-center text-foreground text-sm font-medium focus:outline-none tabular-nums ${lotControl.className}`}
              disabled={lotControl.isDisabled}
              placeholder="0.01"
              aria-label="Lot size"
              {...lotControl.ariaAttrs}
            />

            <button
              onClick={lotControl.guard(() => adjustLotSize(true))}
              className={`p-1.5 hover:bg-gray-800 transition-colors min-w-11 min-h-11 flex items-center justify-center ${lotControl.isDisabled ? "is-disabled-glyph" : ""}`}
              disabled={lotControl.isDisabled}
              aria-label="Increase lots"
              {...lotControl.ariaAttrs}
            >
              <ChevronUp size={14} />
            </button>
          </div>

          {/* BUY Button */}
          <button
            onClick={buyControl.guard(handleBuyByMarket)}
            disabled={!currentQuote || askPrice === 0}
            className={`flex-1 bg-blue-600 hover:bg-blue-700 text-foreground rounded px-2.5 py-1.5 transition-colors min-h-11 ${buyControl.className}`}
            aria-label={`Buy at market, ${formatPrice(askPrice)}`}
            {...buyControl.ariaAttrs}
          >
            <div className="text-[10px] opacity-70">BUY</div>
            <div className="text-base font-bold tabular-nums">{formatPrice(askPrice)}</div>
          </button>
        </div>

        {/* Status Messages - removed processing counter */}

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-1.5 text-[11px] text-red-400 text-center">
            {errorMessage}
          </div>
        )}

        {/* Selected Symbol Display */}
        {selectedSymbol && (
          <div className="mt-1 text-[11px] text-muted-foreground text-center truncate">
            {selectedSymbol.currency}
          </div>
        )}
      </div>

      <OneClickTradingDialog
        open={showOneClickDialog}
        onOpenChange={setShowOneClickDialog}
        onConfirm={handleOneClickConfirm}
      />
    </div>
  );
}
